import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Lock, Mail, Eye, EyeOff, AlertCircle, Crown, 
  Car, Sparkles, ArrowRight, LogIn 
} from 'lucide-react';

const UserLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
          
          {/* Left Side - Branding */}
          <div className="hidden lg:block text-white space-y-8 pr-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <img src="/3.png" alt="شعار الموقع" className="w-10 h-10 text-black object-contain" />
                <div>
                  <h1 className="text-4xl font-bold">LUXURY CAR</h1>
                  <p className="text-yellow-500">خدمات السيارات الفاخرة</p>
                </div>
              </div>

              <h2 className="text-5xl font-bold leading-tight">
                مرحباً بعودتك! 👋
              </h2>
              <p className="text-xl text-gray-300">
                سجل دخولك للوصول إلى خدماتنا المميزة
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="bg-yellow-500/20 p-2 rounded-lg">
                  <Car className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">صيانة منزلية</h3>
                  <p className="text-sm text-gray-400">خدمة سريعة في موقعك</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="bg-orange-500/20 p-2 rounded-lg">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">قطع غيار أصلية</h3>
                  <p className="text-sm text-gray-400">ضمان الجودة والأصالة</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="bg-purple-500/20 p-2 rounded-lg">
                  <Crown className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">خدمة VIP</h3>
                  <p className="text-sm text-gray-400">اهتمام خاص بسيارتك</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full">
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 md:p-12 border border-white/20">
              
              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
                <img src="/3.png" alt="شعار الموقع" className="w-8 h-8 text-black object-contain" />
                <h1 className="text-2xl font-bold text-gray-900">LUXURY CAR</h1>
              </div>

              {/* Form Header */}
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  تسجيل الدخول
                </h2>
                <p className="text-gray-600">
                  مرحباً بك مجدداً في عالم الفخامة
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-shake">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                    البريد الإلكتروني
                  </label>
                  <div className="relative">
                    <Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pr-12 pl-4 py-4 border-2 border-gray-200 rounded-xl
                               focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                               text-right transition-all text-gray-900 placeholder-gray-400"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 text-right">
                    كلمة المرور
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pr-12 pl-12 py-4 border-2 border-gray-200 rounded-xl
                               focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent
                               text-right transition-all text-gray-900"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-yellow-600 hover:text-yellow-700 font-medium transition-colors"
                  >
                    نسيت كلمة المرور؟
                  </button>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500" />
                    <span className="text-gray-700">تذكرني</span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700
                           text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02]
                           disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                           shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      جاري تسجيل الدخول...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      تسجيل الدخول
                    </>
                  )}
                </button>
              </form>

              {/* Register Link */}
              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  ليس لديك حساب؟{' '}
                  <Link
                    to="/register"
                    className="text-yellow-600 hover:text-yellow-700 font-bold transition-colors inline-flex items-center gap-1"
                  >
                    سجل الآن
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </p>
              </div>

              {/* Back to Home */}
              <div className="mt-6 text-center">
                <Link
                  to="/"
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
                >
                  العودة للصفحة الرئيسية
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserLogin;
